import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import Hero from './components/Hero';
import About from './components/About';
import Products from './components/Products';
import ProductPage from './components/ProductPage';
import MSCs from './components/MSCs';
import Contact from './components/Contact';
import Navbar from './components/Navbar';
import SecondNavbar from './components/SecondNavbar';
import { t } from './i18n'; // Import our new auto-translation function
import './App.css';

// Import logo
import logo from './images/first page/cropped-exosomes_transperant.png';

// Component to handle scroll to top on route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Main App content component
function AppContent() {
  const [language, setLanguage] = useState('bg');
  const [theme, setTheme] = useState('light');
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize theme and language on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const savedLanguage = localStorage.getItem('language') || 'bg';
    
    setTheme(savedTheme);
    setLanguage(savedLanguage);
    
    applyTheme(savedTheme);
  }, []);

  // Apply theme to document
  const applyTheme = (newTheme) => {
    const root = document.documentElement;
    root.removeAttribute('data-theme');
    
    if (newTheme === 'light') {
      root.setAttribute('data-theme', 'light');
    } else if (newTheme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    }
  };

  // Handle language change
  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  // Handle theme change
  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme);
  };

  // Show normal Navbar on home and about pages, SecondNavbar on product pages
  const shouldShowNormalNavbar = ['/', '/about'].includes(location.pathname);
  const shouldShowSecondNavbar = ['/products', '/mscs', '/contact'].includes(location.pathname) || 
    location.pathname.startsWith('/product/');

  return (
    <div className="App">
      {shouldShowNormalNavbar && (
        <Navbar language={language} onLanguageChange={handleLanguageChange} />
      )}
      {shouldShowSecondNavbar && (
        <SecondNavbar language={language} onLanguageChange={handleLanguageChange} />
      )}
      <ScrollToTop />
      <main className="main-content">
        <Routes>
          <Route 
            path="/" 
            element={<Hero language={language} />} 
          />
          <Route 
            path="/about" 
            element={<About language={language} />} 
          />
          <Route 
            path="/products" 
            element={<Products language={language} />} 
          />
          <Route 
            path="/product/:productId" 
            element={<ProductPage language={language} />} 
          />
          <Route 
            path="/mscs" 
            element={<MSCs language={language} />} 
          />
          <Route 
            path="/contact" 
            element={<Contact language={language} />} 
          />
        </Routes>
      </main>

      {/* Footer in quiz1 style */}
      <footer>
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <img src={logo} alt="Exosomes Bulgaria" style={{ filter: 'brightness(0) invert(1)', width: '30px', height: '30px' }} />
              <span>EXOREVIVE</span>
            </div>
            <p>{t('Иновативни решения в регенеративната медицина за вашето здраве и красота.', language)}</p>
          </div>
          
          <div className="footer-section">
            <h4>{t('Навигация', language)}</h4>
            <ul>
              <li><Link to="/">{t('Начало', language)}</Link></li>
              <li><Link to="/about">{t('За нас', language)}</Link></li>
              <li><Link to="/products">{t('Продукти', language)}</Link></li>
              <li><Link to="/mscs">{t('Мезенхимни стволови клетки', language)}</Link></li>
              <li><Link to="/contact">{t('Контакт', language)}</Link></li>
            </ul>
          </div>
          
          <div className="footer-section">
            <h4>{t('Продукти', language)}</h4>
            <ul>
              <li><Link to="/products?category=rejuvenation">{t('Подмладяване', language)}</Link></li>
              <li><Link to="/products?category=hair">{t('Коса', language)}</Link></li>
              <li><Link to="/products?category=skincare">{t('Грижа за кожата', language)}</Link></li>
              <li><Link to="/products?category=exosomes">{t('Екзозоми', language)}</Link></li>
            </ul>
          </div>
          
          <div className="footer-section">
            <h4>{t('Контакт', language)}</h4>
            <ul>
              <li>{t('Телефон', language)}: +359 877 488 410</li>
              <li>{t('Имейл', language)}: info@exorevivebulgaria.com</li>
              <li>{t('София, България', language)}</li>
              <li>{t('Работно време', language)}: {t('Понеделник - Петък: 9:00 - 18:00', language)}</li>
            </ul>
          </div>
          
          <div className="footer-section">
            <h4>{t('Следвайте ни', language)}</h4>
            <div className="social-links">
              <a href="https://www.facebook.com/ExosomesBulgaria" target="_blank" rel="noopener noreferrer">
                <img src={require('./images/ig and fb/fbicon.webp')} alt="Facebook" style={{ width: '24px', height: '24px' }} />
              </a>
              <a href="https://www.instagram.com/exorevive_bulgaria/" target="_blank" rel="noopener noreferrer">
                <img src={require('./images/ig and fb/igicon.webp')} alt="Instagram" style={{ width: '24px', height: '24px' }} />
              </a>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; 2024 EXOREVIVE BULGARIA. {t('Всички права запазени.', language)}</p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App; 